import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type IconNameEnum,
  useFintechUiStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import { CoreFintechUiComponents } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import {
  FintechProductInfoLinksSection,
  FintechProductOptionsSection,
  type FintechProductOptionsSectionProps,
  type FintechProductOptionsSectionItemProps,
} from "@components/core-fintech-ui";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import clsx from "clsx";

import defaultStyles from "./FintechProduct.module.scss";
import { FintechProductInfoLinksLayout } from "./models";

/**
 * @deprecated use FintechProductOptionsSectionItemProps instead
 */
export type SelectionOfferItemProp = FintechProductOptionsSectionItemProps;

type InfoLinksUI = {
  /**
   * @deprecated no don't use this prop, use css overrides instead.
   */
  layout?: FintechProductInfoLinksLayout;
  suffixIcon?: { iconName: IconNameEnum; className?: string };
  prefixIcon?: { iconName: IconNameEnum; className?: string };
  displayViewTermsAsModal?: boolean;
};

export interface FintechProductProps {
  title: string;
  iconName: IconNameEnum;
  children?: JSX.Element;
  selectionOffers: {
    options: FintechProductOptionsSectionItemProps[];
  };
  howItWorksDialog?: JSX.Element;
  viewTermsLink: string;
  // To support portals that require different positioning of info links (FintechProductInfoLinksSection)
  infoLinksUI?: InfoLinksUI;
  showCloseButtonInDialog?: boolean;
  collapsable?: boolean;
  footerSubtitle?: ReactNode;
}

type SelectionOffersProps<T extends FintechProductOptionsSectionItemProps> = {
  options: T[];
  Component: React.ComponentType<FintechProductOptionsSectionProps>;
};

export interface FintechProductPropsV2 {
  title: string;
  iconName: IconNameEnum;
  children?: JSX.Element;
  selectionOffers: SelectionOffersProps<any>;
  // To support portals that require different positioning of info links (FintechProductInfoLinksSection)
  infoLinks?: {
    howItWorksDialog?: JSX.Element;
    viewTermsLink?: string;
    ui?: InfoLinksUI;
  };
  showCloseButtonInDialog?: boolean;
  // by default is true, if false then screen will be formatted for mobile
  supportDesktopAndUp?: boolean;
  collapsable?: boolean;
  footerSubtitle?: ReactNode;
}

export const FintechProduct = (props: FintechProductProps) => {
  const v2Props: FintechProductPropsV2 = {
    ...props,
    selectionOffers: {
      options: props.selectionOffers.options,
      Component: FintechProductOptionsSection,
    },
    infoLinks: {
      howItWorksDialog: props.howItWorksDialog,
      viewTermsLink: props.viewTermsLink,
      ui: props.infoLinksUI,
    },
    collapsable: props.collapsable,
  };
  return <FintechProductV2 {...v2Props} />;
};

export const FintechProductV2 = (props: FintechProductPropsV2) => {
  const COMPONENT_KEY = CoreFintechUiComponents.FintechProduct;
  const styles = useFintechUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const [showSelectedOfferSummary, setShowSelectedOfferSummary] =
    useState(false);

  return (
    <div className={clsx(block)}>
      <div
        className={cn("header-container", {
          "show-selected-offer-summary": showSelectedOfferSummary,
        })}
      >
        <Icon
          className={cn("header-image")}
          useCurrentColor={false}
          iconName={props.iconName}
        />
        <h3 className={cn("header-title")}>{props.title}</h3>
      </div>
      <Body
        {...props}
        cn={cn}
        showSelectedOfferSummary={showSelectedOfferSummary}
        setShowSelectedOfferSummary={setShowSelectedOfferSummary}
      />
    </div>
  );
};

const Body = (
  props: FintechProductPropsV2 & {
    cn: (className: string) => string;
    showSelectedOfferSummary: boolean;
    setShowSelectedOfferSummary: (isSeleted: boolean) => void;
  }
) => {
  const {
    cn,
    showSelectedOfferSummary,
    setShowSelectedOfferSummary,
    selectionOffers,
    showCloseButtonInDialog,
    children,
    infoLinks,
    supportDesktopAndUp = true,
    collapsable = true,
  } = props;

  const { isDesktopAndUp: deviceTypeIsDesktop } = useDeviceType();

  const isDesktopAndUp = useMemo(() => {
    return supportDesktopAndUp ? deviceTypeIsDesktop : false;
  }, [supportDesktopAndUp, deviceTypeIsDesktop]);

  const fintechProductInfoLinksSection = useMemo(() => {
    if (infoLinks == null) {
      return null;
    }
    const {
      howItWorksDialog,
      viewTermsLink,
      ui = {
        layout: FintechProductInfoLinksLayout.Horizontal,
        displayViewTermsAsModal: true,
      },
    } = infoLinks;

    return (
      <FintechProductInfoLinksSection
        howItWorksDialog={howItWorksDialog}
        viewTermsLink={viewTermsLink}
        layout={ui.layout}
        showCloseButtonInDialog={showCloseButtonInDialog}
        suffixIcon={ui.suffixIcon}
        prefixIcon={ui.prefixIcon}
        displayViewTermsAsModal={ui.displayViewTermsAsModal}
      />
    );
  }, [infoLinks, showCloseButtonInDialog]);

  const selectedOffer = useMemo(
    () => selectionOffers.options.find((offer) => offer.selected),
    [selectionOffers.options]
  );

  useEffect(
    function autoShowSelectedOfferSummaryGivenPreselectedOfferOnDesktop() {
      if (!isDesktopAndUp) {
        return;
      }

      if (selectedOffer == null) {
        return;
      }

      setShowSelectedOfferSummary(true);
    },
    [isDesktopAndUp, selectedOffer, setShowSelectedOfferSummary]
  );

  const onSelect = useCallback(() => {
    if (!isDesktopAndUp) {
      return;
    }

    setShowSelectedOfferSummary(true);
  }, [isDesktopAndUp, setShowSelectedOfferSummary]);

  return collapsable && selectedOffer != null && showSelectedOfferSummary ? (
    <SelectedOfferSummary
      cn={cn}
      selectedOffer={selectedOffer}
      onEditClick={() => setShowSelectedOfferSummary(false)}
    />
  ) : (
    <div className={cn("body")}>
      <div className={cn("body-information")}>{children}</div>
      <div className={cn("body-options")}>
        <div className={cn("options-title")}>
          <Trans i18nKey="core-fintech-ui.fintech-product.optionsTitle" />
        </div>
        <selectionOffers.Component
          options={selectionOffers.options}
          onSelect={onSelect}
        />
      </div>
      {(fintechProductInfoLinksSection != null ||
        props.footerSubtitle != null) && (
        <div className={cn("body-footer")}>
          {fintechProductInfoLinksSection}
          {props.footerSubtitle != null && (
            <div className={cn("body-footer-right")}>
              {props.footerSubtitle}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SelectedOfferSummary = (props: {
  cn: (className: string) => string;
  selectedOffer: FintechProductOptionsSectionItemProps;
  onEditClick: () => void;
}) => {
  const { t } = useI18nContext();

  const { selectedOffer, onEditClick, cn } = props;
  const icon = selectedOffer.selectedContent.icon;

  return (
    <div className={cn("selected-offer-summary")}>
      {icon != null && (
        <Icon
          iconName={icon.iconName}
          className={clsx(cn("selected-offer-summary-icon"), icon.className)}
          useCurrentColor={false}
        />
      )}

      <div className={cn("selected-offer-summary-content")}>
        {selectedOffer.selectedContent.children}
      </div>

      <button
        onClick={onEditClick}
        className={cn("selected-offer-summary-edit-button")}
      >
        {t("editProtection")}
      </button>
    </div>
  );
};
